// src/pages/Partners/PartnersPage.tsx
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Clapperboard,
  Handshake,
  Mail,
  Sparkles,
  Theater,
  Users,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import StudioSection from '../../components/Studio/StudioSection';
import { StudioCallout } from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  EYEBROW,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import communityService from '../../services/communityService';

const PARTNER_TYPES = [
  {
    icon: Building2,
    title: 'Brands & businesses',
    body: 'A short film, a series or a documentary made with you, carrying your name because you helped make it. Not a pre-roll people skip.',
  },
  {
    icon: Theater,
    title: 'Theatre groups & companies',
    body: 'A streaming home for your work, and a talent pipeline in both directions. Your performers become StrymHub members; our productions cast from your company.',
  },
  {
    icon: Sparkles,
    title: 'Festivals & arts organisations',
    body: 'Co-programmed strands, filmed capture of live work, and a permanent home for pieces that would otherwise vanish after their run.',
  },
  {
    icon: Users,
    title: 'Universities & training providers',
    body: 'Real credits for emerging talent. Your students join the troupe, work on real productions and leave with a body of work that has a URL.',
  },
];

const WAYS_TO_WORK = [
  {
    step: 'Sponsored production',
    body: 'You fund a production; it is made to StrymHub editorial standards and premieres here with your name on the credits and the production page.',
  },
  {
    step: 'Co-production',
    body: 'We build it together — shared cost, shared credit, shared rights. Best fit for organisations with their own creative input.',
  },
  {
    step: 'Talent partnership',
    body: 'Your company or cohort joins the StrymHub repertory. No money changes hands; we cast from your people, you get credits and distribution.',
  },
  {
    step: 'Distribution partnership',
    body: 'You made something good. We give it a home, a page, credits for everyone involved, and an audience that trusts the curation.',
  },
];

const PartnersPage = () => {
  useSEO({
    title: 'Partner With StrymHub Studios — Sponsored & Co-Produced Work',
    description:
      'Brands, theatre groups, festivals and universities: co-produce original film, series and documentary work with StrymHub Studios in Sheffield, and reach an audience that trusts the curation.',
    keywords:
      'brand partnership film, sponsored production uk, co-production sheffield, theatre partnership, arts partnership, branded content studio, indie studio sponsorship',
    type: 'website',
  });

  const stats = useStudioResource((signal) => communityService.getStats(signal), []);

  const heroStats = stats.data
    ? [
        { label: 'Members to cast from', value: String(stats.data.members) },
        { label: 'Productions', value: String(stats.data.productions) },
        { label: 'Crafts in-house', value: String(stats.data.crafts.length) },
      ]
    : undefined;

  return (
    <StudioShell>
      <StudioHero
        eyebrow="Partnerships & sponsorship"
        title="Don't buy ad space."
        accent="Make something."
        lede="StrymHub Studios co-produces original work with brands, theatre groups, festivals and universities. You get real cultural work with your name on it, made by a real crew, hosted somewhere people actually watch it."
        stats={heroStats}
        actions={
          <>
            <a href="mailto:partnerships@strymhub.com" className={PRIMARY_BUTTON}>
              <Mail className="h-4 w-4" aria-hidden="true" />
              Start a conversation
            </a>
            <Link to="/productions" className={SECONDARY_BUTTON}>
              See our work
            </Link>
          </>
        }
      />

      {/* Who we work with */}
      <StudioSection
        eyebrow="Who we partner with"
        title="Four kinds of partner"
        description="Different needs, same principle: your money and your name go into making something real, not into buying attention next to somebody else's work."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {PARTNER_TYPES.map((type) => (
            <div key={type.title} className={`${PANEL} p-6`}>
              <type.icon className="h-6 w-6 text-orange-400" aria-hidden="true" />
              <h3 className="mt-4 font-display text-base font-bold text-white">{type.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">{type.body}</p>
            </div>
          ))}
        </div>
      </StudioSection>

      {/* How it works */}
      <section className="border-y border-white/10 bg-white/[0.015] py-16 sm:py-20">
        <div className={CONTAINER}>
          <p className={EYEBROW}>Ways to work together</p>
          <h2 className="heading-cinematic mt-3 text-2xl text-white sm:text-3xl">
            Four shapes a partnership can take
          </h2>

          <dl className="mt-12 divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {WAYS_TO_WORK.map((way) => (
              <div key={way.step} className="grid gap-3 py-6 md:grid-cols-[220px_1fr] md:gap-10">
                <dt className="font-display text-lg font-bold text-white">{way.step}</dt>
                <dd className="text-sm leading-relaxed text-neutral-400">{way.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* What you get */}
      <StudioSection
        eyebrow="What you get"
        title="What a partner actually walks away with"
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <div className={`${PANEL} p-6`}>
            <Clapperboard className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-base font-bold text-white">
              A finished piece of work
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Written, cast, shot and finished in-house. Yours to use anywhere, permanently hosted
              here with its own production page and full credits.
            </p>
          </div>
          <div className={`${PANEL} p-6`}>
            <Users className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-base font-bold text-white">
              A named cast and crew
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Real people from our repertory, credited by name, who talk about the work publicly
              because it&apos;s their work too.
            </p>
          </div>
          <div className={`${PANEL} p-6`}>
            <Handshake className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-base font-bold text-white">
              Genuine local relevance
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Sheffield-rooted stories with an audience that chose to be here. No programmatic
              inventory, no tracking, no resold data.
            </p>
          </div>
        </div>
      </StudioSection>

      <div className={`${CONTAINER} pb-24`}>
        <StudioCallout
          title="Tell us what you want to make"
          description="We read every enquiry ourselves. Budget ranges are flexible and we're just as interested in a theatre group with no money as a brand with a campaign — as long as the work is worth making."
          action={
            <a href="mailto:partnerships@strymhub.com" className={PRIMARY_BUTTON}>
              partnerships@strymhub.com
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          }
        />
      </div>
    </StudioShell>
  );
};

export default PartnersPage;
